// UI visibility is only a convenience; the review API remains the authority.
export function hasReviewAccess(user, row) {
  const grant = user?.appointment_review;
  const branches = grant?.branches;
  const nonemptyId = (value) => typeof value === 'string' && value.trim().length > 0;
  return Boolean(nonemptyId(user?.tenant_id) && nonemptyId(user?.user_id || user?.id)
    && !['inactive', 'disabled'].includes(user.status) && user.enabled !== false
    && grant?.enabled === true && Array.isArray(branches) && branches.length
    && branches.every(nonemptyId)
    && (!row || (row.tenant_id === user.tenant_id && branches.includes(row.location_id))));
}
// Scope changes remount the feature session before it can render prior evidence.
// Equivalent /auth/me focus responses keep the same session and in-flight guard.
export function reviewIdentity(user) {
  if (!hasReviewAccess(user)) return null;
  return JSON.stringify({
    user_id: user.user_id || user.id,
    tenant_id: user.tenant_id,
    appointment_review: { enabled: true, branches: [...new Set(user.appointment_review.branches)].sort() },
  });
}
export const reviewIsLive = (row) => !row.review_tombstone
  && !['cancelled', 'canceled', 'cancelada', 'cancelado', 'no_show', 'deleted'].includes(String(row.status || '').trim().toLowerCase());
export const isPendingReview = (row) => row.advisor_review_enrolled === true
  && row.advisor_review_status === 'pending' && reviewIsLive(row);

// No floating-point multiplication or permissive parseFloat: explicit MXN cents.
export function priceToMinor(value) {
  const match = /^(\d{1,7})(?:\.(\d{1,2}))?$/.exec(String(value).trim());
  if (!match) return null;
  const cents = Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
  return cents > 0 && cents <= 999999999 ? cents : null;
}
export function formatMinor(value) {
  if (!Number.isInteger(value) || value < 1 || value > 999999999) return '';
  return Math.floor(value / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    + '.' + String(value % 100).padStart(2, '0');
}

// Oct 19 client call (José, angle C part 3): ADICIONALES -- extra line items the advisor adds
// BEFORE confirming ({descripcion, precio}) so the customer sees the real total. Mirrors
// appointment_advisor_review.normalize_adicionales / total_minor: the request carries
// `price_minor` = BASE and `adicionales` = [{descripcion, precio_minor}]; the server persists
// price_minor = base + sum(adicionales) in review_final.payload (plus base_price_minor /
// adicionales / adicionales_minor) and the final WhatsApp renders that total.
export const ADICIONALES_LIMIT = 20;
export const ADICIONAL_DESCRIPCION_MAX = 80;
export const PRICE_MINOR_MAX = 999999999;
// The fixed Meta-approved body only has the {{8}} amount variable free, so the breakdown is
// the suffix inside it (mirrors appointment_advisor_review.ADICIONALES_SUFFIX / format_total).
export const ADICIONALES_SUFFIX = ' (incluye adicionales)';
export const emptyAdicional = () => ({ descripcion: '', precio: '' });
// Editor rows -> request items; null when ANY row is invalid (never silently drop a line).
// Rows that are completely blank (both fields empty) are ignored, so a stray empty line never blocks.
export function adicionalesToRequest(rows) {
  const items = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    const descripcion = String(row?.descripcion || '').replace(/\s+/g, ' ').trim();
    const precio = String(row?.precio ?? '').trim();
    if (!descripcion && !precio) continue;
    const precio_minor = priceToMinor(precio);
    if (!descripcion || descripcion.length > ADICIONAL_DESCRIPCION_MAX || precio_minor === null) return null;
    items.push({ descripcion, precio_minor });
  }
  return items.length > ADICIONALES_LIMIT ? null : items;
}
export const adicionalesMinor = (items) => (Array.isArray(items) ? items : [])
  .reduce((sum, item) => sum + (Number.isInteger(item?.precio_minor) ? item.precio_minor : 0), 0);
// base + sum(adicionales); null when either side is invalid or the total passes the money ceiling.
export function totalMinor(baseMinor, items) {
  if (!Number.isInteger(baseMinor) || baseMinor < 1 || items === null) return null;
  const total = baseMinor + adicionalesMinor(items);
  return total > PRICE_MINOR_MAX ? null : total;
}
// Mirrors appointment_advisor_review.format_total: the amount the final renders in {{8}}.
export function formatTotal(totalMinorValue, hasAdicionales) {
  const amount = formatMinor(totalMinorValue);
  return amount ? '$' + amount + (hasAdicionales ? ADICIONALES_SUFFIX : '') : '';
}

// Mirrors appointment_advisor_review.final_text. Preserve booking's wall time,
// not the browser timezone. Approved text always comes from the durable payload.
// td244 (Roger spec 2026-10-07 21:15 CST): the final WhatsApp names the slot long-form
// es_MX ("viernes 9 de octubre", "10:00 am") and the LOGGED-IN approver, never the
// cartera asesor on the row. Mirrors appointment_advisor_review.final_text (+ FINAL_CLOSING).
// Copy v2 (Hugo 2026-10-08): usted register; names the customer, the service the bot read in
// the opener and the unit + kilometraje (frozen in review_pending.payload.booking at enrolment;
// rows enrolled before v2 fall back to the row's service / vehicle without the VIN fragment).
export const FINAL_CLOSING = 'Si necesita cambiarla, responda a este mensaje. ¡Gracias!';
// Copy v3 (Jocelin's confirmación, Roger 2026-10-15 18:10 "apply"): client line layout, unit without
// kilometraje, "Costo aproximado: $X MXN con IVA incluido". Rendered ONLY for a row whose hold snapshot
// says copy_version 3 (review_pending.payload.booking.copy_version, mirrors
// appointment_advisor_review.final_text / COPY_VERSION); every other row keeps the v2 text.
export const ARRIVAL_NOTE = 'Le estaremos esperando 10 min antes de su cita; por favor ingrese con las intermitentes '
  + 'encendidas y, si cuenta con birlo de seguridad, déjelo en su portavasos. En todo caso, es requerida su '
  + 'constancia de situación fiscal.';
export const FINAL_CLOSING_V3 = ARRIVAL_NOTE + ' ' + FINAL_CLOSING;
export const COPY_VERSION_V3 = 3;
export function copyVersion(booking) {
  const value = booking?.review_pending?.payload?.booking?.copy_version;
  return Number(value) === COPY_VERSION_V3 ? COPY_VERSION_V3 : 2;
}
export const NAME_FALLBACK = 'estimado cliente';
export const SERVICE_FALLBACK = 'servicio de mantenimiento';
export const VEHICLE_FALLBACK = 'su vehículo';
export function serviceLabel(booking) {
  const hold = booking?.review_pending?.payload?.booking || {};
  return String(hold.service_label || '').trim() || String(booking?.service || '').trim() || SERVICE_FALLBACK;
}
export function vehicleLabel(booking) {
  // oct15 (Hugo, td369): modelo + año only -- the client-approved "confirmación" copy has no kilometraje
  // (mirrors appointment_advisor_review.vehicle_label; a pre-oct15 hold snapshot keeps the label it named).
  const hold = booking?.review_pending?.payload?.booking || {};
  if (String(hold.vehicle_label || '').trim()) return String(hold.vehicle_label).trim();
  return String(booking?.vehicle || '').replace(/\s*\bVIN\b.*$/i, '').replace(/^\s*chevrolet\s+/i, '').trim() || VEHICLE_FALLBACK;
}
const HONORIFICS = new Set(['sr', 'sra', 'srta', 'lic', 'ing', 'dr', 'dra', 'don', 'dona']);
const PARTICLES = new Set(['de', 'del', 'la', 'las', 'los', 'y']);
const fold = (t) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\.$/, '');
const caseToken = (t) => (t === t.toUpperCase() || t === t.toLowerCase() ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : t);
export function greetingName(value) {
  // oct15 (Hugo td369 CAf8550e7dfb "Hola Hugo Villaseñor"): {{1}} is the FIRST name through the same rule
  // the backend sender uses (postventa_contacts.customer_display_name): internal " - TAG" suffix and
  // digit tokens ("V19") dropped, honorifics skipped, particle chain kept ("Maria del Carmen"), Title Case.
  // '' when nothing name-like remains (caller renders NAME_FALLBACK).
  const cleaned = String(value || '').split(/\s+[-–—|/]\s+/)[0];
  const tokens = cleaned.split(/\s+/).map((t) => t.replace(/^[^\p{L}]+|[^\p{L}]+$/gu, ''))
    .filter((t) => t.length >= 2 && !/\d/.test(t));
  while (tokens.length && HONORIFICS.has(fold(tokens[0]))) tokens.shift();
  let i = 0;
  while (i < tokens.length && PARTICLES.has(fold(tokens[i]))) i += 1;
  if (i >= tokens.length) return '';
  const out = [caseToken(tokens[i])];
  let j = i + 1;
  if (j < tokens.length && PARTICLES.has(fold(tokens[j]))) {
    while (j < tokens.length && PARTICLES.has(fold(tokens[j]))) { out.push(tokens[j].toLowerCase()); j += 1; }
    if (j < tokens.length) out.push(caseToken(tokens[j]));
  }
  return out.join(' ');
}
const DIAS_ES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
  'septiembre', 'octubre', 'noviembre', 'diciembre'];
export function formatSlotEs(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/.exec(String(value || ''));
  if (!m || !validReviewDate(value)) return null;
  const date = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));   // booked wall date, browser-tz independent
  const hour = +m[4];
  return {
    date: DIAS_ES[date.getUTCDay()] + ' ' + date.getUTCDate() + ' de ' + MESES_ES[date.getUTCMonth()],
    time: String(hour % 12 || 12) + ':' + m[5] + ' ' + (hour < 12 ? 'am' : 'pm'),
  };
}
// `priceMinor` is the TOTAL the message renders; `hasAdicionales` appends the breakdown suffix
// (an approved row passes review_final.payload.price_minor / adicionales.length > 0).
export function finalMessagePreview(booking, priceMinor, advisorName, hasAdicionales = false) {
  const total = formatTotal(priceMinor, Boolean(hasAdicionales));
  if (!total) return '';
  if (['customer_phone', 'service', 'start_datetime', 'sucursal']
    .some((key) => !String(booking[key] || '').trim())) return '';
  const advisor = String(advisorName === undefined ? booking.assigned_advisor : advisorName || '').trim();
  if (!advisor) return '';
  const slot = formatSlotEs(booking.start_datetime);
  if (!slot) return '';
  const name = greetingName(booking.customer_name) || NAME_FALLBACK;
  const text = copyVersion(booking) === COPY_VERSION_V3
    ? 'Hola ' + name + ' 👋 Le confirmamos su cita de servicio en Chevrolet ' + booking.sucursal + '.\n'
      + '🗓️ Fecha: ' + slot.date + ' a las ' + slot.time + '\n'
      + '🚗 Unidad: ' + vehicleLabel(booking) + '\n'
      + '🛠️ Servicio: ' + serviceLabel(booking) + '\n'
      + '💲 Costo aproximado: ' + total + ' MXN con IVA incluido\n'
      + '👤 Asesor de servicio: ' + advisor + '\n'
      + FINAL_CLOSING_V3
    : 'Hola ' + name + ', su cita de ' + serviceLabel(booking) + ' (' + vehicleLabel(booking) + ') quedó confirmada para el '
      + slot.date + ' a las ' + slot.time + ' en la sucursal ' + booking.sucursal + '. Precio confirmado por su asesor '
      + advisor + ': ' + total + ' MXN (IVA incluido). ' + FINAL_CLOSING;
  return ['{{', '}}', '@{'].some((token) => text.includes(token)) ? '' : text;
}
export function reviewErrorKey(error) {
  const status = error?.response?.status;
  if (status === 401 || status === 403) return 'denied';
  if (status === 404) return 'notFound';
  if (status === 409) return 'stale';
  if (status === 422) return 'invalid';
  return 'requestFailed';
}

// Preserve the booked wall time, independent of the browser's timezone.
function validReviewDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/.exec(String(value || ''));
  if (!match || Number.isNaN(Date.parse(value))) return false;
  const date = new Date(Date.UTC(+match[1], +match[2] - 1, +match[3]));
  return date.getUTCFullYear() === +match[1] && date.getUTCMonth() === +match[2] - 1 && date.getUTCDate() === +match[3];
}

export function formatReviewDate(value, locale = 'en') {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/.exec(String(value || ''));
  if (!match || !validReviewDate(value)) return '';
  const date = new Date(Date.UTC(+match[1], +match[2] - 1, +match[3]));
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date)
    + ' · ' + match[4] + ':' + match[5];
}
export function formatEstimate(value, locale = 'en') {
  if (!/^(\d+)(?:\.\d{1,2})?$/.test(String(value)) || !Number.isFinite(Number(value)) || Number(value) <= 0) return '';
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'MXN', currencyDisplay: 'code' }).format(Number(value));
}
